import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Alert,
  Box,
  Card,
  CardContent,
  CardActionArea,
  Divider,
  Stack,
  Avatar,
  alpha,
  TextField,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import {
  Business as BusinessIcon,
  PersonAdd as PersonAddIcon,
  Link as LinkIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useState, useMemo, useEffect } from 'react';
import type { CompanyReviewDecision, CompanyMatch } from '../../types/companyReview';
import { CompanyReviewDecisionType } from '../../types/companyReview';
import type { CompanyReviewWithMatches } from '../../types/companyReview';
import InstructionsPanel from './InstructionsPanel';
import CompanyList from './CompanyList';
import {
  CancelWarningDialog,
  MapConfirmationDialog,
} from './ConfirmationDialogs';

interface CompanyReviewModalProps {
  request: CompanyReviewWithMatches | null;
  onClose: () => void;
  onSubmit: (decision: CompanyReviewDecision) => Promise<void>;
  totalReviews?: number;
  currentReviewNumber?: number;
}

const getMatchColor = (score: number) => {
  if (score >= 90) return 'success';
  if (score >= 75) return 'warning';
  return 'default';
};

const CompanyReviewModal: React.FC<CompanyReviewModalProps> = ({
  request,
  onClose,
  onSubmit,
  totalReviews = 1,
  currentReviewNumber = 1,
}) => {
  const showProgress = totalReviews > 1;
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [decision, setDecision] = useState<CompanyReviewDecisionType>(
    CompanyReviewDecisionType.MapToExisting,
  );
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const [showMapConfirmation, setShowMapConfirmation] = useState(false);

  const matchingCompanies: CompanyMatch[] = request?.matchingCompanies ?? [];

  useEffect(() => {
    if (request?.reviewId) {
      setSelectedCompany(null);
      setDecision(CompanyReviewDecisionType.MapToExisting);
      setHasAutoSelected(false);
      setSearchQuery('');
      setError(null);
      setShowCancelWarning(false);
      setShowMapConfirmation(false);
    }
  }, [request?.reviewId]);

  useEffect(() => {
    if (
      matchingCompanies.length > 0 &&
      !selectedCompany &&
      !hasAutoSelected &&
      decision === CompanyReviewDecisionType.MapToExisting
    ) {
      const highestMatch = matchingCompanies.reduce((prev, current) =>
        (current.similarityScore ?? 0) > (prev.similarityScore ?? 0)
          ? current
          : prev,
      );
      if (highestMatch?.companyId) {
        setSelectedCompany(highestMatch.companyId);
        setHasAutoSelected(true);
      }
    }
  }, [matchingCompanies, selectedCompany, hasAutoSelected, decision]);

  const filteredCompanies = useMemo(() => {
    if (!searchQuery.trim()) return matchingCompanies;
    const query = searchQuery.toLowerCase();
    return matchingCompanies.filter(
      company =>
        company.companyName?.toLowerCase().includes(query) ||
        company.companyGST?.toLowerCase().includes(query),
    );
  }, [matchingCompanies, searchQuery]);

  if (!request) return null;

  const handleSubmit = async () => {
    if (decision === CompanyReviewDecisionType.MapToExisting && !selectedCompany) {
      setError('Please select a company to map to');
      return;
    }
    if (decision === CompanyReviewDecisionType.MapToExisting) {
      setShowMapConfirmation(true);
      return;
    }
    await executeSubmit();
  };

  const executeSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      await onSubmit({
        ReviewId: request.reviewId,
        Decision: decision,
        SelectedCompanyId:
          decision === CompanyReviewDecisionType.MapToExisting && selectedCompany
            ? selectedCompany
            : undefined,
        NewCompanyName:
          decision === CompanyReviewDecisionType.CreateNew
            ? request.extractedCompanyName
            : undefined,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to submit company review';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmMap = async () => {
    setShowMapConfirmation(false);
    await executeSubmit();
  };

  const handleDecisionChange = (newDecision: CompanyReviewDecisionType) => {
    setDecision(newDecision);
    setError(null);
    if (newDecision === CompanyReviewDecisionType.CreateNew) {
      setSelectedCompany(null);
    }
  };

  const handleCancelClick = () => {
    if (!loading) {
      setShowCancelWarning(true);
    }
  };

  const handleConfirmCancel = async () => {
    setShowCancelWarning(false);
    setLoading(true);
    setError(null);
    try {
      await onSubmit({
        ReviewId: request.reviewId,
        Decision: CompanyReviewDecisionType.CreateNew,
        NewCompanyName: request.extractedCompanyName,
      });
      handleClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create company';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedCompany(null);
      setDecision(CompanyReviewDecisionType.MapToExisting);
      setError(null);
      setSearchQuery('');
      setShowCancelWarning(false);
      onClose();
    }
  };

  const handleSelectCompany = (companyId: string) => {
    setSelectedCompany(companyId);
    setError(null);
  };

  const selectedCompanyName =
    matchingCompanies.find(c => c.companyId === selectedCompany)?.companyName ??
    'Selected Company';

  return (
    <>
      <Dialog
        open={!!request}
        onClose={handleCancelClick}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden' },
        }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white',
            p: 2.5,
          }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: alpha('#fff', 0.2), width: 44, height: 44 }}>
              <BusinessIcon />
            </Avatar>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography variant="h6" fontWeight="bold" fontSize="1.1rem">
                  Company Review Required
                </Typography>
                {showProgress && (
                  <Chip
                    label={`${currentReviewNumber} of ${totalReviews}`}
                    size="small"
                    sx={{
                      bgcolor: alpha('#fff', 0.2),
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                    }}
                  />
                )}
              </Stack>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {showProgress
                  ? `Review ${currentReviewNumber} of ${totalReviews} companies`
                  : 'Match or create a new company entry'}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex' }}>
          <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
            <InstructionsPanel />

            <Box sx={{ flex: 1, p: 2.5, overflow: 'auto' }}>
              <Card
                variant="outlined"
                sx={{
                  mb: 2.5,
                  bgcolor: alpha('#1976d2', 0.04),
                  borderColor: alpha('#1976d2', 0.2),
                }}>
                <CardContent sx={{ py: 1.5, px: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    textTransform="uppercase"
                    fontWeight="medium"
                    fontSize="0.65rem">
                    Extracted Company Name
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="primary"
                    fontWeight="bold"
                    sx={{
                      mt: 0.25,
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}>
                    {request.extractedCompanyName}
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {request.companyGST && (
                      <Typography variant="caption" color="text.secondary">
                        GST: <strong>{request.companyGST}</strong>
                      </Typography>
                    )}
                    {request.fileName && (
                      <Typography variant="caption" color="text.secondary">
                        File: <strong>{request.fileName}</strong>
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 2, borderRadius: 2, py: 0.5 }}>
                  {error}
                </Alert>
              )}

              <RadioGroup
                value={decision}
                onChange={e =>
                  handleDecisionChange(
                    e.target.value as CompanyReviewDecisionType,
                  )
                }>
                <Card
                  variant="outlined"
                  sx={{
                    mb: 1.5,
                    borderColor:
                      decision === CompanyReviewDecisionType.MapToExisting
                        ? 'primary.main'
                        : 'divider',
                    borderWidth:
                      decision === CompanyReviewDecisionType.MapToExisting ? 2 : 1,
                    transition: 'all 0.2s ease',
                  }}>
                  <CardActionArea
                    onClick={() =>
                      handleDecisionChange(CompanyReviewDecisionType.MapToExisting)
                    }
                    sx={{ p: 0 }}>
                    <CardContent sx={{ py: 1.5, px: 2 }}>
                      <FormControlLabel
                        value="MapToExisting"
                        control={<Radio size="small" />}
                        label={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}>
                            <LinkIcon color="primary" fontSize="small" />
                            <Typography fontWeight="medium" fontSize="0.9rem">
                              Map to existing company
                            </Typography>
                          </Stack>
                        }
                        sx={{ m: 0 }}
                      />
                    </CardContent>
                  </CardActionArea>

                  {decision === CompanyReviewDecisionType.MapToExisting &&
                    matchingCompanies.length > 0 && (
                      <>
                        <Divider />
                        <Box sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                          <Autocomplete
                            options={matchingCompanies}
                            getOptionLabel={(option: CompanyMatch) =>
                              option.companyName || ''
                            }
                            filterOptions={(options, { inputValue }) => {
                              const query = inputValue.toLowerCase();
                              return options.filter(
                                option =>
                                  option.companyName
                                    ?.toLowerCase()
                                    .includes(query) ||
                                  option.companyGST
                                    ?.toLowerCase()
                                    .includes(query),
                              );
                            }}
                            inputValue={searchQuery}
                            onInputChange={(_, value) => setSearchQuery(value)}
                            onChange={(_, value) => {
                              if (value) {
                                setSelectedCompany(value.companyId);
                                setSearchQuery(value.companyName || '');
                                setError(null);
                              }
                            }}
                            autoHighlight
                            autoSelect
                            selectOnFocus
                            handleHomeEndKeys
                            openOnFocus
                            blurOnSelect
                            clearOnBlur={false}
                            renderOption={(props, option: CompanyMatch) => (
                              <Box
                                component="li"
                                {...props}
                                key={option.companyId}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  width="100%">
                                  <Box>
                                    <Typography
                                      fontSize="0.875rem"
                                      fontWeight="medium">
                                      {option.companyName}
                                    </Typography>
                                    {option.companyGST && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary">
                                        {option.companyGST}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Chip
                                    label={`${option.similarityScore}%`}
                                    size="small"
                                    color={getMatchColor(
                                      option.similarityScore,
                                    )}
                                    sx={{
                                      fontWeight: 'bold',
                                      fontSize: '0.7rem',
                                    }}
                                  />
                                </Stack>
                              </Box>
                            )}
                            renderInput={params => (
                              <TextField
                                {...params}
                                placeholder="Search companies..."
                                size="small"
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon
                                        fontSize="small"
                                        sx={{ color: 'text.secondary' }}
                                      />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  mb: 1.5,
                                  '& .MuiOutlinedInput-root': {
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                  },
                                }}
                              />
                            )}
                          />

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="medium"
                            sx={{ mb: 1, display: 'block' }}
                            fontSize="0.65rem">
                            {filteredCompanies.length} MATCHING COMPANY
                            {filteredCompanies.length !== 1 ? 'IES' : ''}
                          </Typography>

                          <CompanyList
                            companies={filteredCompanies}
                            selectedCompany={selectedCompany}
                            onSelectCompany={handleSelectCompany}
                          />
                        </Box>
                      </>
                    )}
                </Card>

                <Card
                  variant="outlined"
                  sx={{
                    borderColor:
                      decision === CompanyReviewDecisionType.CreateNew
                        ? 'primary.main'
                        : 'divider',
                    borderWidth:
                      decision === CompanyReviewDecisionType.CreateNew ? 2 : 1,
                    transition: 'all 0.2s ease',
                  }}>
                  <CardActionArea
                    onClick={() =>
                      handleDecisionChange(CompanyReviewDecisionType.CreateNew)
                    }
                    sx={{ p: 0 }}>
                    <CardContent sx={{ py: 1.5, px: 2 }}>
                      <FormControlLabel
                        value="CreateNew"
                        control={<Radio size="small" />}
                        label={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}>
                            <PersonAddIcon color="primary" fontSize="small" />
                            <Typography fontWeight="medium" fontSize="0.9rem">
                              Create new company
                            </Typography>
                          </Stack>
                        }
                        sx={{ m: 0 }}
                      />
                      {decision === CompanyReviewDecisionType.CreateNew && (
                        <Box sx={{ ml: 4, mt: 0.5 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontSize="0.8rem"
                            sx={{
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                            }}>
                            New company:{' '}
                            <Typography
                              component="span"
                              fontWeight="medium"
                              color="text.primary">
                              "{request.extractedCompanyName}"
                            </Typography>
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </RadioGroup>
            </Box>
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: 'grey.50' }}>
          <Button
            onClick={handleCancelClick}
            disabled={loading}
            variant="outlined"
            size="small"
            color="warning"
            sx={{ borderRadius: 2, px: 2.5 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="small"
            disabled={
              loading ||
              (decision === CompanyReviewDecisionType.MapToExisting &&
                !selectedCompany)
            }
            sx={{ borderRadius: 2, px: 2.5, minWidth: 100 }}>
            {loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Confirm'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <CancelWarningDialog
        open={showCancelWarning}
        onClose={() => setShowCancelWarning(false)}
        onConfirm={handleConfirmCancel}
        loading={loading}
        companyName={request.extractedCompanyName}
      />

      <MapConfirmationDialog
        open={showMapConfirmation}
        onClose={() => setShowMapConfirmation(false)}
        onConfirm={handleConfirmMap}
        loading={loading}
        extractedCompanyName={request.extractedCompanyName}
        selectedCompanyName={selectedCompanyName}
      />
    </>
  );
};

export default CompanyReviewModal;
